import { parsePhoneNumberFromString } from "libphonenumber-js";

export const formatCurrency = (value) => {
  // A missing price (null/undefined/"") is not R0.00, so it must not render as
  // one. It says so in words: an em dash is not allowed anywhere in this
  // product, and a lone dash in a price column is ambiguous anyway, it reads as
  // "free" or "zero" just as easily as "unknown".
  if (value == null || value === "" || Number.isNaN(Number(value))) return "No price";
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
  return `R${formatted}`;
};

// Long-form date, "14 March, 2024". Moved here from
// components/Cart/CartPage/SellerSection.jsx, which B5 deleted: SellerStore
// was the only remaining consumer and it had no business importing a helper
// out of the cart's seller cell.
//
// FLAG, not fixed here: the default timeZone is "Asia/Karachi" on a South
// African marketplace, so a join date can render a day out. It is carried over
// verbatim because changing it changes what /seller/:sellerId renders, and
// that page belongs to another batch.
export const formatPrettyDate = (iso, timeZone = "Asia/Karachi") => {
  if (!iso) return "N/A";

  const d = new Date(iso);
  if (isNaN(d.getTime())) return "N/A";

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone,
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value])
  );

  return `${parts.day} ${parts.month}, ${parts.year}`;
};

// The API returns media (card/catalog images) as relative paths like
// "/media/catalog/cards/....jpg". Those resolve against the document origin
// (the SPA), not the API, so they 404 whenever the frontend and backend are on
// different origins (e.g. localhost:3000 vs localhost:8000). Prefix backend
// media paths with the API origin; leave absolute URLs and frontend-local
// assets (/placeholder.svg, /figmaAssets/...) alone.
export const mediaUrl = (path) => {
  if (!path) return path;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (path.startsWith("/media")) {
    const base = process.env.REACT_APP_API_URI || "";
    return `${base}${path}`;
  }
  return path;
};

export const formatNumber = (value, decimals = 2) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export function formatPhoneNumber(value, previousValue = "", country = "ZA") {
  if (!value) return "";
  
  const digitsOnly = value.replace(/\D/g, "");
  const prevDigitsOnly = previousValue.replace(/\D/g, "");
  
  const isDeleting = digitsOnly.length < prevDigitsOnly.length;
  
  if (digitsOnly.length > 11) {
    const limitedDigits = digitsOnly.slice(0, 11);
    return formatPhoneNumber(limitedDigits, "", country);
  }
  
  if (digitsOnly.length === 0) {
    return "";
  }
  
  if (isDeleting) {
    if (digitsOnly.startsWith('27') && digitsOnly.length >= 2) {
      if (digitsOnly.length <= 2) return `+${digitsOnly}`;
      if (digitsOnly.length <= 4) return `+27 ${digitsOnly.slice(2)}`;
      if (digitsOnly.length <= 7) return `+27 ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4)}`;
      return `+27 ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7)}`;
    } else if (digitsOnly.startsWith('0')) {
      if (digitsOnly.length <= 3) return digitsOnly;
      if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
      return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
    }
    return digitsOnly;
  }
  
  if (digitsOnly.startsWith('27') && digitsOnly.length >= 3) {
    if (digitsOnly.length <= 3) return `+27 ${digitsOnly.slice(2)}`;
    if (digitsOnly.length <= 5) return `+27 ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4)}`;
    if (digitsOnly.length <= 8) return `+27 ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7)}`;
    return `+27 ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 11)}`;
  } else if (digitsOnly.startsWith('0')) {
    if (digitsOnly.length <= 3) return digitsOnly;
    if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
    return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 10)}`;
  } else if (digitsOnly.startsWith('2')) {
    return `+${digitsOnly}`;
  }
  
  return digitsOnly;
}

export function isValidPhoneNumber(value, country = "ZA") {
  if (!value) return false;
  
  const digitsOnly = value.replace(/\D/g, "");
  
  const validPrefixes = ['060', '061', '062', '063', '064', '065', '066', '067', '068', '069', 
                        '070', '071', '072', '073', '074', '076', '078', '079', '081', '082', 
                        '083', '084', '085', '086', '087'];
  
  if (digitsOnly.length < 10) {
    if (digitsOnly.startsWith('0')) {
      if (digitsOnly.length >= 3) {
        const prefix = digitsOnly.slice(0, 3);
        return validPrefixes.includes(prefix);
      }
      return digitsOnly.length <= 3;
    }
    if (digitsOnly.startsWith('27')) {
      if (digitsOnly.length >= 4) {
        const prefix = '0' + digitsOnly.slice(2, 4);
        return validPrefixes.includes(prefix);
      }
      return digitsOnly.length <= 4;
    }
    if (digitsOnly.startsWith('2')) {
      return digitsOnly.length <= 4;
    }
    return false;
  }
  
  if (digitsOnly.length > 11) {
    return false;
  }
  
  let prefix;
  if (digitsOnly.startsWith('27') && digitsOnly.length === 11) {
    prefix = '0' + digitsOnly.slice(2, 4);
  } else if (digitsOnly.startsWith('0') && digitsOnly.length === 10) {
    prefix = digitsOnly.slice(0, 3);
  } else {
    return false;
  }
  
  const isValidPrefix = validPrefixes.includes(prefix);
  
  try {
    const phoneNumber = parsePhoneNumberFromString(value, country);
    return phoneNumber ? phoneNumber.isValid() && isValidPrefix : isValidPrefix;
  } catch (err) {
    return isValidPrefix;
  }
}

export function getPhoneNumberError(value, country = "ZA") {
  if (!value || !value.trim()) {
    return "Phone number is required";
  }
  
  const digitsOnly = value.replace(/\D/g, "");
  
  if (digitsOnly.length < 10) {
    return "Phone number is too short";
  }
  
  if (digitsOnly.length > 11) {
    return "Phone number is too long";
  }
  
  if (!isValidPhoneNumber(value, country)) {
    return "Enter a valid SA phone number (e.g. +27 71 234 5678 or 071 234 5678)";
  }
  
  return null;
}
